import { useState, useEffect, useCallback } from "react";
import { Bot, CheckCircle, XCircle, Clock, Filter, Loader2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface AuditEntry {
  id: string;
  action_type: string;
  entity_type: string;
  entity_id: string | null;
  model_used: string;
  prompt_version: string;
  status: string;
  triggered_by: string;
  human_edited: boolean;
  approved: boolean | null;
  published: boolean;
  latency_ms: number | null;
  tokens_in: number | null;
  tokens_out: number | null;
  created_at: string;
  error_message: string | null;
  input_summary: Record<string, unknown> | null;
  output_summary: Record<string, unknown> | null;
}

export default function AIAuditTrailPanel() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    let query = (supabase.from("ai_audit_trail").select("*"))
      .order("created_at", { ascending: false })
      .limit(100);
    if (statusFilter !== "all") query = query.eq("status", statusFilter);
    if (entityFilter !== "all") query = query.eq("entity_type", entityFilter);
    const { data } = await query;
    setEntries((data as AuditEntry[]) || []);
    setLoading(false);
  }, [statusFilter, entityFilter]);

  useEffect(() => { load(); }, [load]);

  const statusIcon = (s: string) => {
    if (s === "success") return <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />;
    if (s === "error" || s === "rejected") return <XCircle className="w-3.5 h-3.5 text-destructive" />;
    return <Clock className="w-3.5 h-3.5 text-amber-400" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-lg text-foreground">AI Audit Trail</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[120px] h-8 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-[140px] h-8 text-xs"><SelectValue placeholder="Entity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="lesson">Lesson</SelectItem>
              <SelectItem value="module">Module</SelectItem>
              <SelectItem value="assessment">Assessment</SelectItem>
              <SelectItem value="tutor">Tutor</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="h-8 text-xs gap-1">
            <Filter className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 gap-2">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <span className="text-sm text-muted-foreground">Loading audit trail…</span>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No audit entries found.
        </div>
      ) : (
        <div className="space-y-1">
          {entries.map(e => (
            <button
              key={e.id}
              onClick={() => setSelected(e)}
              className="w-full text-left flex items-center gap-3 rounded-lg border border-border/40 bg-card/50 p-3 hover:bg-card/80 transition-colors"
            >
              {statusIcon(e.status)}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-heading font-semibold text-foreground truncate">
                  {e.action_type} · {e.entity_type}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {e.model_used} · v{e.prompt_version} · {e.latency_ms ? `${e.latency_ms}ms` : "—"}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {e.human_edited && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 font-heading">Edited</span>}
                {e.approved && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-heading">Approved</span>}
                {e.published && <span className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-heading">Published</span>}
                <span className="text-[10px] text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</span>
                <Eye className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Audit Entry Detail</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <Detail label="Action" value={selected.action_type} />
                <Detail label="Entity Type" value={selected.entity_type} />
                <Detail label="Model" value={selected.model_used} />
                <Detail label="Prompt Version" value={selected.prompt_version} />
                <Detail label="Status" value={selected.status} />
                <Detail label="Triggered By" value={selected.triggered_by} />
                <Detail label="Latency" value={selected.latency_ms ? `${selected.latency_ms}ms` : "—"} />
                <Detail label="Tokens" value={`${selected.tokens_in || 0} in / ${selected.tokens_out || 0} out`} />
                <Detail label="Human Edited" value={selected.human_edited ? "Yes" : "No"} />
                <Detail label="Approved" value={selected.approved === null ? "Pending" : selected.approved ? "Yes" : "No"} />
                <Detail label="Published" value={selected.published ? "Yes" : "No"} />
                <Detail label="Created" value={new Date(selected.created_at).toLocaleString()} />
              </div>
              {selected.error_message && (
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-2">
                  <p className="text-[10px] font-semibold text-destructive uppercase mb-0.5">Error</p>
                  <p className="text-muted-foreground">{selected.error_message}</p>
                </div>
              )}
              {selected.input_summary && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Input Summary</p>
                  <pre className="bg-muted/30 rounded-lg p-2 overflow-x-auto text-[10px] text-foreground/70 whitespace-pre-wrap">
                    {JSON.stringify(selected.input_summary, null, 2)}
                  </pre>
                </div>
              )}
              {selected.output_summary && (
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Output Summary</p>
                  <pre className="bg-muted/30 rounded-lg p-2 overflow-x-auto text-[10px] text-foreground/70 whitespace-pre-wrap">
                    {JSON.stringify(selected.output_summary, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      <p className="font-heading font-semibold text-foreground">{value}</p>
    </div>
  );
}
